/* eslint-disable prefer-rest-params */
const APP_ID = import.meta.env.VITE_REACT_APP_INTERCOM_KEY;

let isIntercomLoaded = false;
let isIntercomLoading = false;

// Function to fix Intercom widget accessibility issues
const fixIntercomAccessibility = () => {
  // Wait for Intercom widget to load
  setTimeout(() => {
    const intercomFrame = document.querySelector(
      "#intercom-frame",
    ) as HTMLIFrameElement;
    if (intercomFrame) {
      // Add proper accessibility attributes to Intercom iframe
      intercomFrame.setAttribute("title", "Intercom live chat messenger");
      intercomFrame.setAttribute("aria-label", "Live chat support");
    }

    // Find Intercom launcher button and ensure it has proper accessibility
    const intercomLauncher = document.querySelector(
      '[aria-label*="Open Intercom"], [data-intercom-target="intercom-frame"]',
    );
    if (intercomLauncher && !intercomLauncher.getAttribute("aria-label")) {
      intercomLauncher.setAttribute("aria-label", "Open live chat support");
    }
  }, 2000); // Wait 2 seconds for Intercom to fully load
};

/**
 * Initialize Intercom for Single Page Apps (SPA)
 * Based on: https://www.intercom.com/help/en/articles/170-install-intercom-in-your-product-for-logged-in-users
 *
 * This sets up the Intercom JS library and handles reattaching on page changes
 */
export const initIntercomSettings = () => {
  const Window = window as any;

  // Check if Intercom is already initialized (for SPA reattach)
  const ic = Window.Intercom;
  if (typeof ic === "function") {
    ic("reattach_activator");
    ic("update", Window.intercomSettings);
    return;
  }

  // Create Intercom function stub for queuing calls before script loads
  const IntercomFunctionBuilder: any = function () {
    IntercomFunctionBuilder.c(arguments);
  };
  IntercomFunctionBuilder.q = [];
  IntercomFunctionBuilder.c = function (args: any) {
    IntercomFunctionBuilder.q.push(args);
  };
  Window.Intercom = IntercomFunctionBuilder;

  // Store default settings to be used when Intercom boots
  Window.intercomSettings = {
    api_base: "https://api-iam.intercom.io",
    app_id: APP_ID,
    hide_default_launcher: true, // Hide default launcher since we're using custom button
  };
};

/**
 * Lazy load Intercom script - only loads when explicitly called
 * This improves initial page load performance
 */
export const loadIntercomLazy = async (): Promise<void> => {
  // Prevent multiple loads
  if (isIntercomLoaded || isIntercomLoading) {
    return;
  }

  isIntercomLoading = true;

  // Load Intercom script
  return new Promise((resolve, reject) => {
    const appScript = document.createElement("script");
    appScript.type = "text/javascript";
    appScript.async = true;
    appScript.src = `https://widget.intercom.io/widget/${APP_ID}`;

    appScript.addEventListener("load", () => {
      isIntercomLoaded = true;
      isIntercomLoading = false;
      fixIntercomAccessibility();
      resolve();
    });

    appScript.addEventListener("error", () => {
      isIntercomLoading = false;
      reject(new Error("Failed to load Intercom script"));
    });

    const x = document.getElementsByTagName("script")[0];
    x.parentNode?.insertBefore(appScript, x);
  });
};

/**
 * Boot Intercom with user data after lazy loading
 * Call this after the user has authenticated
 *
 * @param customSettings - User data to pass to Intercom (email, user_id, name, etc.)
 */
export const bootIntercomAfterLoad = (customSettings?: {
  email?: string;
  user_id?: string;
  name?: string;
  created_at?: number;
  user_hash?: string; // For identity verification
  [key: string]: any; // Allow custom attributes
}) => {
  const Window = window as any;
  const Intercom = Window.Intercom;

  if (typeof Intercom === "function") {
    const settings = {
      app_id: APP_ID,
      api_base: "https://api-iam.intercom.io",
      hide_default_launcher: true,
      ...customSettings,
    };

    Intercom("boot", settings);
    Intercom("show");
  }
};

/**
 * Update Intercom when user data or URL changes
 * Call this on route changes or when user data updates in your SPA
 *
 * Note: Limited to 20 calls per user per page before throttling
 *
 * @param userData - Updated user data (only include changed fields)
 */
export const updateIntercom = (userData?: { [key: string]: any }) => {
  const Window = window as any;
  const Intercom = Window.Intercom;

  if (typeof Intercom === "function") {
    if (userData) {
      Intercom("update", userData);
    } else {
      // Trigger a ping without data change (for URL changes)
      Intercom("update", {
        last_request_at: Math.floor(Date.now() / 1000),
      });
    }
  }
};

/**
 * Shutdown Intercom session
 * IMPORTANT: Call this when user logs out to clear session data and cookies
 * This prevents the next user from seeing previous user's conversation history
 */
export const shutdownIntercom = () => {
  const Window = window as any;
  const Intercom = Window.Intercom;

  if (typeof Intercom === "function") {
    Intercom("shutdown");
  }
};

/**
 * Show the Intercom Messenger
 */
export const showIntercom = () => {
  const Window = window as any;
  const Intercom = Window.Intercom;

  if (typeof Intercom === "function") {
    Intercom("show");
  }
};

/**
 * Hide the Intercom Messenger
 */
export const hideIntercom = () => {
  const Window = window as any;
  const Intercom = Window.Intercom;

  if (typeof Intercom === "function") {
    Intercom("hide");
  }
};

/**
 * Show Intercom with a new message pre-populated
 *
 * @param message - Pre-populated message text
 */
export const showNewMessage = (message?: string) => {
  const Window = window as any;
  const Intercom = Window.Intercom;

  if (typeof Intercom === "function") {
    if (message) {
      Intercom("showNewMessage", message);
    } else {
      Intercom("showNewMessage");
    }
  }
};

/**
 * Track an event in Intercom
 *
 * @param eventName - Name of the event
 * @param metadata - Optional metadata about the event
 */
export const trackEvent = (
  eventName: string,
  metadata?: { [key: string]: any },
) => {
  const Window = window as any;
  const Intercom = Window.Intercom;

  if (typeof Intercom === "function") {
    if (metadata) {
      Intercom("trackEvent", eventName, metadata);
    } else {
      Intercom("trackEvent", eventName);
    }
  }
};

// Legacy function for backward compatibility
export const bootIntercom = () => {
  initIntercomSettings();
};
